import type { PsdSeries } from '../../types';
import {
  DEFAULT_WELCH_SAMPLE_RATE_HZ,
  WELCH_NFFT,
  WELCH_OVERLAP,
  WELCH_WINDOW,
} from './constants';

const round = (value: number, digits = 6) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const average = (values: number[]) => {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const hannWindow = (length: number) =>
  Array.from(
    { length },
    (_, index) => 0.5 * (1 - Math.cos((2 * Math.PI * index) / (length - 1)))
  );

export const computeWelchPsd = (
  values: number[],
  sampleRateHz: number = DEFAULT_WELCH_SAMPLE_RATE_HZ
): PsdSeries => {
  if (values.length === 0) {
    return { frequencies: [], psd_lf: [], psd_hf: [] };
  }

  const windowLength = Math.min(WELCH_WINDOW, values.length);
  const step = Math.max(1, windowLength - WELCH_OVERLAP);
  const window = hannWindow(windowLength);
  const halfNfft = WELCH_NFFT / 2;
  const psd = new Array<number>(halfNfft + 1).fill(0);
  let segmentCount = 0;

  for (let start = 0; start + windowLength <= values.length; start += step) {
    const segment = values.slice(start, start + windowLength);
    const segmentMean = average(segment);
    const detrended = segment.map(
      (value, index) => (value - segmentMean) * window[index]
    );

    for (
      let frequencyIndex = 0;
      frequencyIndex <= halfNfft;
      frequencyIndex += 1
    ) {
      let real = 0;
      let imaginary = 0;

      for (let sampleIndex = 0; sampleIndex < windowLength; sampleIndex += 1) {
        const angle =
          (-2 * Math.PI * frequencyIndex * sampleIndex) / WELCH_NFFT;
        real += detrended[sampleIndex] * Math.cos(angle);
        imaginary += detrended[sampleIndex] * Math.sin(angle);
      }

      psd[frequencyIndex] += (real ** 2 + imaginary ** 2) / windowLength;
    }

    segmentCount += 1;
  }

  const normalized = psd.map(value =>
    segmentCount > 0 ? value / segmentCount : 0
  );
  const frequencies = normalized.map((_, frequencyIndex) =>
    round((frequencyIndex * sampleRateHz) / WELCH_NFFT)
  );

  return {
    frequencies,
    psd_lf: normalized,
    psd_hf: normalized,
  };
};
